// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: fanArtLikes/$likeId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Likes on fan art submissions. One like per wallet per submission, enforced by frontend using composite IDs. Public read, authenticated create, owner can unlike (delete).
 */

export interface FanArtLikesRequest {
  submissionId: string;
  walletAddress: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface FanArtLikesResponse {
  submissionId: string;
  walletAddress: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a FanArtLikes operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildFanArtLikes(likeId: string, data?: FanArtLikesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `fanArtLikes/${likeId}`, document: doc };
}

/**
 * Authenticated users only. The walletAddress field must match the caller's wallet address. submissionId, walletAddress, and createdAt are immutable after creation. Frontend should use a composite ID like '{walletAddress}_{submissionId}' (using underscore, NOT hyphens) to enforce one like per wallet per submission. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setFanArtLikes(likeId: string, data?: FanArtLikesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`fanArtLikes/${likeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting FanArtLikes: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
   (Get Single Item)
 */
export async function getFanArtLikes(likeId: string): Promise<FanArtLikesResponse | null> {
  try {
    const result = await get(`fanArtLikes/${likeId}`);
    return result as FanArtLikesResponse | null;
  } catch (error) {
    console.error(`Error getting FanArtLikes: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single FanArtLikes document. (
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
  )
 */
export function subscribeFanArtLikes(callback: (data: FanArtLikesResponse | null) => void, likeId: string): Promise<() => Promise<void>> {
  return subscribe(`fanArtLikes/${likeId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as FanArtLikesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to FanArtLikes at path '${`fanArtLikes/${likeId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many FanArtLikes items from collection fanArtLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
  
 */
export async function getManyFanArtLikes(filter: string = ""): Promise<FanArtLikesResponse[]> {
  try {
    const results = await get("fanArtLikes", { prompt: filter });
    return Array.isArray(results) ? results as FanArtLikesResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtLikes collection at path 'fanArtLikes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtLikes collection at fanArtLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
  
 */
export function subscribeManyFanArtLikes(callback: (data: FanArtLikesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtLikes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtLikesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtLikes collection at path '${"fanArtLikes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all FanArtLikes items from collection fanArtLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
  
 */
export async function getAllFanArtLikes(filter: string = ""): Promise<FanArtLikesResponse[]> {
  try {
    const results = await get("fanArtLikes", { prompt: filter });
    return Array.isArray(results) ? results as FanArtLikesResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtLikes collection at path 'fanArtLikes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtLikes collection at fanArtLikes
 
  Read Operation Details: Anyone can read likes. Used to display like counts and whether the current user has liked a fan art submission.
  
 */
export function subscribeAllFanArtLikes(callback: (data: FanArtLikesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtLikes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtLikesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtLikes collection at path '${"fanArtLikes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count FanArtLikes items in collection fanArtLikes.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countFanArtLikes(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("fanArtLikes", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting FanArtLikes at path 'fanArtLikes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on FanArtLikes items in collection fanArtLikes.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateFanArtLikes(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("fanArtLikes", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating FanArtLikes at path 'fanArtLikes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the liker (matching walletAddress) can remove their like to unlike a submission.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteFanArtLikes(likeId: string): Promise<boolean> {
  try {
    const succeeded = await set(`fanArtLikes/${likeId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting FanArtLikes: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for FanArtLikes for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteFanArtLikes(likeId: string): DocumentOperation {
  return { path: `fanArtLikes/${likeId}`, document: null };
}

